import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter/src/TypesenseInstantsearchAdapter"
import { getAdditionalSearchParameters } from "@jobshop/crossroads/utils/search/additionalSearchParameters"
import { checkLocationFacetFilter, checkLocationFilter, getActiveLocationValues } from "@jobshop/crossroads/utils/search/handleLocationFilters"
import { facetableFields } from "@jobshop/crossroads/utils/search/facetableFields"
import { escapeFacetFilterBackticks } from "@/utils/escapeFacetFilterBackticks"
import { TYPESENSE_REQUEST_TIMEOUT_MS, withTypesenseKeyRetry } from "@/utils/typesenseRetry"

function buildAdapterOptions(apiKey: string, typesenseUrl: string, fullTextSearch: boolean) {
  return {
    server: {
      apiKey,
      nodes: [
        {
          url: typesenseUrl,
        },
      ],
      cacheSearchResultsForSeconds: 2 * 60,
      connectionTimeoutSeconds: TYPESENSE_REQUEST_TIMEOUT_MS / 1000,
    },
    geoLocationField: "coordinate",
    facetableFieldsWithSpecialCharacters: facetableFields,
    additionalSearchParameters: getAdditionalSearchParameters("default", [], fullTextSearch),
  }
}

// `refreshTypesenseApiKey` refreshes the key cached under the caller's own filter
// (useTypesenseKeyRefresh with that cache key). It is injected rather than resolved
// here because callers invoke this after an await in async setup().
export async function useTypesenseSearchClient(searchConfig: SearchConfig, apiKey: string, refreshTypesenseApiKey: () => Promise<string>) {
  const { public: { typesenseUrl } } = useRuntimeConfig()

  // Required for handling nationwide jobs
  const { nationwideText } = searchConfig.filters

  // Whether to also search the long-form job body fields (JET-2595). Opt-in per
  // job shop / module, so it has to be threaded into the adapter options: the
  // static `additionalSearchParameters` export is built with it hardcoded off.
  // Each container passes the one config the mixin's precedence rule resolves to
  // — the module's in SearchAndFilterModuleContainer, the page-wide one in
  // InstantSearchContainer — so this stays in sync with `isFullTextSearchEnabled`.
  const fullTextSearch = searchConfig.search?.fullTextSearch === true

  const typesenseInstantsearchAdapter = ref(
    new TypesenseInstantSearchAdapter(buildAdapterOptions(apiKey, typesenseUrl, fullTextSearch)),
  )

  // The adapter holds the key itself, so the operation ignores the key argument
  // and the refresh step reconfigures the adapter with the new one.
  async function refreshAdapterKey(): Promise<string> {
    const newKey = await refreshTypesenseApiKey()
    typesenseInstantsearchAdapter.value.updateConfiguration(buildAdapterOptions(newKey, typesenseUrl, fullTextSearch))
    return newKey
  }

  function withAdapterRetry<T>(fn: () => Promise<T>): Promise<T> {
    return withTypesenseKeyRetry(() => fn(), apiKey, refreshAdapterKey)
  }

  const searchClient = ref({
    ...typesenseInstantsearchAdapter.value.searchClient,
    search: async (requests: any[]) => {
      const activeLocations = [...new Set(
        requests.flatMap(request => getActiveLocationValues(request, nationwideText)),
      )]

      const boostedSortBy = activeLocations.length > 0
        ? getAdditionalSearchParameters("default", activeLocations).sort_by
        : ""

      const newRequests = requests.map((request) => {
        checkLocationFacetFilter(request, nationwideText)
        checkLocationFilter(request, nationwideText)

        // Escape backticks in refined facet values before the adapter wraps them.
        escapeFacetFilterBackticks(request)

        // Inject location boost via the adapter's index name sort convention
        // e.g. "offers/sort/expr" → adapter extracts "expr" as sort_by
        if (boostedSortBy)
          request.indexName = `${request.indexName}/sort/${boostedSortBy}`

        return request
      })

      return withAdapterRetry(() => typesenseInstantsearchAdapter.value.searchClient.search(newRequests))
    },
    searchForFacetValues: async (...args: any[]) => {
      return withAdapterRetry(() => typesenseInstantsearchAdapter.value.searchClient.searchForFacetValues(...args))
    },
  })

  return { typesenseInstantsearchAdapter, searchClient }
}
